import React from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Milestones, ActionButtons } from '.'
import { SocialMedia, ShareButtons, ButtonOutline, Button, User } from '../../../components'
import LikedByPopup from '../../../features/collection/components/liked-by-popup'
import { useInternationalization } from '../../../features/i18n'
import service from '../../../features/individu/service'
import { SystemProvider } from '../../../features/system/provider'
import { errorHandler, useViewport } from '../../../utils'

const Sidebar = () => {
  const { isDesktop } = useViewport()
  const { translate, language } = useInternationalization()
  const translation = useSelector(state => state.translation.data)
  const profile = useSelector(state => state.account.profile)
  const organization = useSelector(state => state.organization.data)
  const [requesting, setRequesting] = React.useState(false)
  const { isLoggedIn, isOfficial, isVerified } = useSelector(state => state.account)
  const canJoin = isLoggedIn && !isOfficial && !requesting
  const [showLikes, setShowLikes] = React.useState(false)
  const { pathname } = useLocation()

  const {
    socialLinks,
    milestones,
    website,
    total_member,
    members,
    likes,
    branches,
    phone,
    email,
    pin_points
  } = organization

  const handleJoin = () => {
    if(profile.userId && organization.id){
      setRequesting(true)
      service.join(profile.userId, organization.id)
        .then(res => {
          toast.success('Join request sent!')
        })
        .catch(err => {
          console.log({err})
          const msg = errorHandler(err)
          toast.error(msg)
        })
        .finally(() => setRequesting(false))
    } else {
      toast.error('Error 400: User ID or Organization ID is not provided.')
    }

    if(!canJoin){
      toast.error('Join request already sent.')
    }
  }

  return (
    <>
      <>
        {isVerified || isOfficial ? (
          isDesktop && <ActionButtons />
        ) : (
          <>
            <Button className="mb-5 lg:mb-0" onClick={() => setShowLikes(true)}>
              Show Likes
            </Button>
            {showLikes && (
              <LikedByPopup onClose={() => setShowLikes(false)} data={likes} />
            )}
          </>
        )}
        {isDesktop && <hr className="my-8 lg:my-10" />}
      </>
      {socialLinks && socialLinks.length > 0 && (
        <>
          <SystemProvider>
            <SocialMedia data={socialLinks} website={website} />
          </SystemProvider>
          <hr className="mt-6 mb-8 lg:mt-8 lg:mb-10" />
        </>
      )}

      {website && (
        <>
          {/* <h3 className={`lg:text-left font-bold lg:text-lg font-accent mb-4 lg:mb-5 uppercase`}>
            Website
          </h3> */}
          <a
            className="block mb-4"
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            title={website}
          >
            <ButtonOutline type="button" title={website}>
              Website
            </ButtonOutline>
          </a>
        </>
      )}

      {email && (
        <a className="block mb-4" href={`mailto:${email}`} title={email}>
          <ButtonOutline type="button" title={email}>
            {translate("AUTH", "email")}
          </ButtonOutline>
        </a>
      )}

      {phone && (
        <a href={`tel:${phone}`} title={phone}>
          <ButtonOutline type="button" title={phone}>
            {translate("GENERAL", "phone")}
          </ButtonOutline>
        </a>
      )}

      {(website || phone || email) && <hr className="my-8 lg:my-10" />}

      <div>
        <h3
          className={`lg:text-left font-bold lg:text-lg font-accent uppercase mb-4 lg:mb-5`}
        >
          {translation.GENERAL.location[language.name]} ({pin_points.length})
        </h3>
        {pin_points &&
          pin_points.length > 0 &&
          pin_points.map((p) => (
            <a
              key={p.id}
              className="block mb-4"
              href={`https://google.com/maps/?q=${p.coordinate.lat},${p.coordinate.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              title={p.name}
            >
              <ButtonOutline type="button" title={p.name}>
                {p.name}
              </ButtonOutline>
            </a>
          ))}
      </div>

      <hr className="my-8 lg:my-10" />

      <div>
        <h3
          className={`lg:text-left font-bold lg:text-lg font-accent uppercase mb-4 lg:mb-5`}
        >
          {translation.ORGANIZATION.branches[language.name]} ({branches.length})
        </h3>
        {branches &&
          branches.length > 0 &&
          branches.map((m, index) => (
            <div className="mb-4">
              <User
                small
                organizationId={m.id}
                key={index}
                name={m.name}
                image={m.image}
              />
            </div>
          ))}
      </div>

      <hr className="my-8 lg:my-10" />

      <div>
        <div className="flex items-center justify-between mb-4 lg:mb-5">
          <h3
            className={`lg:text-left font-bold lg:text-lg font-accent uppercase`}
          >
            {translation.MEMBER.plural[language.name]} ({total_member})
          </h3>
          <Link to={pathname + "/member"} className="link text-sm underline">
            {translation.RELATED.view_all[language.name]}
          </Link>
        </div>
        {members && members.length > 0 && (
          <>
            <div className="flex flex-wrap">
              {members.map((m, index) => (
                <div
                  className="bg-no-repeat bg-center rounded-full shadow mr-4 mb-5"
                  style={{
                    backgroundImage: `url('${m.image}')`,
                    backgroundSize: "100%",
                    width: "40px",
                    height: "40px",
                  }}
                  key={index}
                  title={m.name}
                ></div>
              ))}
            </div>
          </>
        )}
        <ButtonOutline
          transparent
          outlineColor="primary"
          className="text-primary uppercase font-accent py-3 text-sm"
          onClick={handleJoin}
          disabled={!canJoin}
          isLoading={requesting}
        >
          {translation.ORGANIZATION.ask_to_join[language.name]}
        </ButtonOutline>
      </div>
      {milestones && milestones.length > 0 && (
        <React.Fragment>
          <hr className="my-8 lg:my-10" />
          <Milestones data={milestones} />
        </React.Fragment>
      )}
      <hr className="my-8 lg:my-10" />

      <div className="lg:mb-10">
        <p className="text-xs font-bold mb-5 text-center lg:text-left">
          {translation.ORGANIZATION.share[language.name]}
        </p>
        <ShareButtons url={window.location.href} />
      </div>
    </>
  );
}

export { Sidebar }
